import {
  normalizeRangeToGranularity,
  type DateRangeType,
  type PeriodGranularityType,
  type PeriodReferentialIntervalType
} from '#domain/core/filter/period';
import type { Dayjs } from 'dayjs';
import type { RangeBoundType } from '../types/periodPopover.type';

function clampDate(date: Dayjs, interval: PeriodReferentialIntervalType): Dayjs {
  if (date.isBefore(interval.start)) {
    return interval.start;
  }

  return date.isAfter(interval.end) ? interval.end : date;
}

function normalizeAndClamp(
  range: DateRangeType,
  granularity: PeriodGranularityType,
  interval: PeriodReferentialIntervalType
): DateRangeType {
  const normalized = normalizeRangeToGranularity(range, granularity);

  return { start: clampDate(normalized.start, interval), end: clampDate(normalized.end, interval) };
}

/**
 * Turns a typed start/end date into the next range: replace the edited bound, normalize to the
 * granularity, then clamp both bounds into the referential interval. When the edited bound ends up
 * after (or before) the other one, the range collapses to the single unit of the edited bound instead
 * of surfacing an inverted range.
 */
export function applyRangeBound(
  range: DateRangeType | null,
  bound: RangeBoundType,
  date: Dayjs,
  granularity: PeriodGranularityType,
  interval: PeriodReferentialIntervalType
): DateRangeType {
  const base = range ?? { start: date, end: date };
  const replaced = bound === 'start' ? { start: date, end: base.end } : { start: base.start, end: date };
  const candidate = normalizeAndClamp(replaced, granularity, interval);

  return candidate.start.isAfter(candidate.end)
    ? normalizeAndClamp({ start: date, end: date }, granularity, interval)
    : candidate;
}
